/**
 * API Client
 * Handles all API requests with error handling and response parsing
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * Custom API error class
 */
class ApiError extends Error {
  constructor(message, status, code, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Make an API request
 */
async function request(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  // Attach JWT token if available
  const token = localStorage.getItem('feedback_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      // Handle 401 Unauthorized - clear token and redirect to login
      if (response.status === 401) {
        localStorage.removeItem('feedback_token');
        localStorage.removeItem('feedback_agent');
        window.location.href = '/login';
      }

      throw new ApiError(
        data.error?.message || 'An error occurred',
        response.status,
        data.error?.code || 'UNKNOWN_ERROR',
        data.error?.details
      );
    }

    return data;
  } catch (err) {
    if (err instanceof ApiError) {
      throw err;
    }

    // Network error or other issue
    throw new ApiError(
      'Unable to connect to server',
      0,
      'NETWORK_ERROR'
    );
  }
}

/**
 * Download a file from an authenticated endpoint (CSV, etc.).
 * Plain browser navigation (window.open / anchor href) can't attach the JWT
 * from localStorage, so those hit 401. Instead fetch with the Authorization
 * header, turn the response into a blob, and trigger the download from it.
 */
async function downloadFile(endpoint, filename) {
  const token = localStorage.getItem('feedback_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('feedback_token');
      localStorage.removeItem('feedback_agent');
      window.location.href = '/login';
    }
    throw new ApiError('Failed to download file', response.status, 'DOWNLOAD_ERROR');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * API methods
 */
const api = {
  // Health
  health: () => request('/health'),

  // Dashboard
  dashboard: (period = 'last_month') => request(`/dashboard?period=${period}`),
  emergingTrends: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/emerging-trends${query ? `?${query}` : ''}`);
  },

  // Clusters
  clusters: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/clusters${query ? `?${query}` : ''}`);
  },
  cluster: (id) => request(`/clusters/${id}`),
  clusterRegional: (id) => request(`/clusters/${id}/regional`),
  updateClusterStatus: (id, data) => request(`/clusters/${id}/status`, {
    method: 'PATCH',
    body: data,
  }),
  // Push a cluster's requesters into a Klaviyo list. dryRun previews the count
  // without writing. Builds the list only — sending happens in Klaviyo.
  klaviyoSyncCluster: (id, { dryRun = false } = {}) =>
    request(`/clusters/${id}/klaviyo-sync${dryRun ? '?dryRun=1' : ''}`, { method: 'POST' }),

  // Search
  search: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/search${query ? `?${query}` : ''}`);
  },
  ticket: (id) => request(`/tickets/${id}`),
  feed: (params = {}) => request(`/feed?${new URLSearchParams(Object.entries(params).filter(([, v]) => v !== '' && v != null))}`),

  // Admin
  refresh: (data) => request('/refresh', {
    method: 'POST',
    body: data,
  }),
  refreshStatus: (jobId) => request(`/refresh/${jobId}`),
  digestLatest: () => request('/digest/latest'),
  generateDigest: (data) => request('/digest/generate', {
    method: 'POST',
    body: data,
  }),
  actionLog: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/action-log${query ? `?${query}` : ''}`);
  },
  processingLog: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/processing-log${query ? `?${query}` : ''}`);
  },

  // Feishu Bitable
  feishuStatus: () => request('/feishu/status'),
  feishuSync: () => request('/feishu/sync', { method: 'POST' }),
  feishuSyncCluster: (id) => request(`/feishu/sync/${id}`, { method: 'POST' }),

  // Auth
  login: (email, password) => request('/auth/login', {
    method: 'POST',
    body: { email, password },
  }),

  // Rankings & Top Requests
  rankings: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/rankings${query ? `?${query}` : ''}`);
  },
  excludeCluster: (id, reason) => request(`/clusters/${id}/exclude`, {
    method: 'PATCH',
    body: { reason },
  }),
  unexcludeCluster: (id) => request(`/clusters/${id}/unexclude`, {
    method: 'PATCH',
  }),

  // Deep health / status page — returns the payload for BOTH 200 and 503
  // (a red overall status is a 503 but the body is still valid health data).
  healthDeep: async () => {
    const token = localStorage.getItem('feedback_token');
    const res = await fetch(`${API_BASE}/health/deep`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 401) {
      localStorage.removeItem('feedback_token');
      localStorage.removeItem('feedback_agent');
      window.location.href = '/login';
      throw new ApiError('Unauthorized', 401, 'UNAUTHORIZED');
    }
    if (res.status === 403) throw new ApiError('Admin or leadership access required', 403, 'FORBIDDEN');
    return res.json();
  },

  // Authenticated file downloads (CSV, etc.)
  downloadFile,
  exportClusterEmails: (id) => downloadFile(`/clusters/${id}/emails?format=csv`, `cluster-${id}-feedback.csv`),
  exportDashboardCsv: (type, period) => downloadFile(`/export/csv?type=${type}&period=${period}`, `feedback-${type}-${period}.csv`),
};

export { api, ApiError };
export default api;
